"use client";

import { useState, useEffect, useCallback } from "react";
import { useTranslations } from "next-intl";
import Image from "next/image";
import { cn } from "@/lib/utils";

import worldMapImg from "@/../public/image/new-version/home/world_map.png";

const DEFAULT_FACTORY_IMAGES = [
	"/image/new-version/machining/five-axis/network/cnc-machining-shop-1.jpg",
	"/image/new-version/machining/five-axis/network/cnc-machining-shop-2.jpg",
	"/image/new-version/machining/five-axis/network/cnc-machining-shop-3.jpg",
	"/image/new-version/machining/five-axis/network/cnc-machining-shop-4.jpg",
	"/image/new-version/machining/five-axis/network/cnc-machining-shop-5.jpg",
];

const DEFAULT_STATS = [
	{ valueKey: "stat1Value", labelKey: "stat1Label" },
	{ valueKey: "stat2Value", labelKey: "stat2Label" },
	{ valueKey: "stat3Value", labelKey: "stat3Label" },
];

export interface GlobalNetworkStat {
	valueKey: string;
	labelKey: string;
}

export interface GlobalNetworkProps {
	/** 翻译命名空间 */
	translationNamespace?: string;
	/** 图片数组 */
	images?: string[];
	/** 统计数据配置 */
	stats?: GlobalNetworkStat[];
	/**
	 * 默认布局下：为 true 时使用两段正文（键名 description1、description2），否则仅用单段 description
	 * 不影响其它页面现有翻译
	 */
	dualDescription?: boolean;
	/**
	 * 布局变体
	 * - "default": 默认布局，左侧文字+统计，右侧图片轮播
	 * - "statsRight": 统计数据在右侧，无图片轮播
	 */
	variant?: "default" | "statsRight";
	/**
	 * 仅 variant=statsRight：标题单行 t("title") 或两行 t("titleLine1")/t("titleLine2") + 第二行右侧短绿线（与折弯能力标题一致）
	 */
	titleLayout?: "single" | "splitAccent";
	className?: string;
}

export default function GlobalNetwork({
	translationNamespace = "fiveAxisGlobalNetwork",
	images,
	stats = DEFAULT_STATS,
	dualDescription = false,
	variant = "default",
	titleLayout = "single",
	className,
}: GlobalNetworkProps) {
	const t = useTranslations(translationNamespace);
	const [currentIndex, setCurrentIndex] = useState(0);

	const factoryImages = images && images.length > 0 ? images : DEFAULT_FACTORY_IMAGES;

	const nextSlide = useCallback(() => {
		setCurrentIndex((prev) => (prev + 1) % factoryImages.length);
	}, [factoryImages.length]);

	useEffect(() => {
		if (variant === "default") {
			const timer = setInterval(nextSlide, 4000);
			return () => clearInterval(timer);
		}
	}, [nextSlide, variant]);

	// statsRight 变体布局
	if (variant === "statsRight") {
		return (
			<section
				className={cn(
					"relative isolate overflow-hidden bg-[#ffffff] py-8 forced-color-adjust-none [color-scheme:light] sm:py-10 md:py-12 lg:py-16",
					className,
				)}
				style={{ colorScheme: "light" }}
			>
				{/* 纯白底避免 WebKit/Chromium 在深色模式/强制对比度下透明区域合成发黑；再叠地图 */}
				<div className="pointer-events-none absolute inset-0 z-0 bg-[#ffffff]">
					<div className="container relative h-full min-h-[200px]">
						<Image
							src={worldMapImg}
							alt="World Map Background"
							fill
							className="object-contain object-center opacity-60"
							sizes="(max-width: 640px) 100vw, (max-width: 768px) 640px, (max-width: 1024px) 768px, (max-width: 1280px) 1024px, 1280px"
						/>
					</div>
				</div>

				{/* 内容区域 */}
				<div className="container relative z-10">
					<div className="flex flex-col gap-8 lg:flex-row lg:items-start lg:justify-between lg:gap-12 xl:gap-16">
						{/* 左侧文字内容（约 40% 宽，与右侧数据约 60%） */}
						<div className="lg:max-w-[40%] lg:shrink-0 xl:max-w-[42%]">
							{/* 标题 */}
							{titleLayout === "splitAccent" ? (
								<h2 className="mb-3 text-2xl font-bold leading-tight text-black sm:mb-4 sm:text-[30px] md:text-4xl">
									<span className="block leading-[1.12]">{t("titleLine1" as any)}</span>
									<span className="mt-0.5 flex items-end gap-2.5 leading-[1.12] sm:gap-3">
										<span>{t("titleLine2" as any)}</span>
										{/* <span
                                            className="h-[3px] w-[3.25rem] shrink-0 rounded-full bg-themePrimary400 sm:w-[3.75rem]"
                                            aria-hidden
                                        /> */}
									</span>
								</h2>
							) : (
								<h2 className="mb-3 text-2xl font-bold leading-tight text-black sm:mb-4 sm:text-[30px] md:text-4xl">
									{t("title")}
								</h2>
							)}

							{/* 描述文字 */}
							<div className="space-y-4 text-sm leading-relaxed text-black md:text-base">
								<p>{t("description1")}</p>
								<p>{t("description2")}</p>
							</div>
						</div>

						{/* 右侧统计数据 */}
						<div className="flex min-w-0 flex-1 flex-wrap justify-start gap-6 sm:gap-8 lg:flex-nowrap lg:justify-between lg:gap-8 xl:gap-12">
							{stats.map((stat, index) => (
								<div key={index} className="text-center lg:text-left">
									<div className="mb-6 text-2xl font-bold text-themePrimary400 sm:text-3xl md:text-4xl lg:text-[42px]">
										{t(stat.valueKey)}
									</div>
									<div className="whitespace-nowrap text-sm text-black md:text-base">{t(stat.labelKey)}</div>
								</div>
							))}
						</div>
					</div>
				</div>
			</section>
		);
	}

	// 默认布局（与 statsRight 一致：显式浅色底 + color-scheme / forced-color-adjust，缓解 Chromium 系在深色模式、
	// 强制对比度或合成层下把半透明区域打成纯黑，而 Firefox 表现正常的问题）
	return (
		<section
			className={cn(
				"relative isolate overflow-hidden bg-[#ffffff] py-8 forced-color-adjust-none [color-scheme:light] sm:py-10 md:py-12 lg:py-16",
				className,
			)}
			style={{ colorScheme: "light" }}
		>
			{/* 世界地图背景：白底 + min-height 保证 fill 图片在 Blink/WebKit 中有确定高度，避免整块发黑 */}
			<div className="pointer-events-none absolute inset-0 z-0 bg-[#ffffff]">
				<div className="container relative h-full min-h-[260px] md:min-h-[360px] lg:min-h-[400px]">
					<Image
						src={worldMapImg}
						alt="World Map Background"
						fill
						className="object-contain object-center"
						sizes="(max-width: 640px) 100vw, (max-width: 768px) 640px, (max-width: 1024px) 768px, (max-width: 1280px) 1024px, 1280px"
					/>
				</div>
			</div>

			{/* 内容区域 */}
			<div className="container relative z-10">
				<div className="grid gap-8 lg:grid-cols-3 lg:gap-12">
					{/* 左侧内容 */}
					<div className="max-w-3xl lg:col-span-2">
						{/* 标题 */}
						<h2 className="mb-3 text-2xl font-bold leading-tight text-black sm:mb-4 sm:text-[30px] md:text-4xl">
							{t("title")}
						</h2>

						{/* 描述文字：单段 description 或双段 description1 + description2 */}
						{dualDescription ? (
							<div className="mb-8 space-y-4 text-sm leading-relaxed text-black md:mb-10 md:text-base">
								<p>{t("description1")}</p>
								<p>{t("description2")}</p>
							</div>
						) : (
							<p className="mb-8 text-sm leading-relaxed text-black md:mb-10 md:text-base">
								{t("description")}
							</p>
						)}

						{/* 统计数据 */}
						<div className="flex flex-wrap gap-8 md:gap-12 lg:gap-16">
							{stats.map((stat, index) => (
								<div key={index} className="text-left">
									<div className="mb-4 text-2xl font-bold text-themePrimary400 sm:text-3xl md:text-4xl">
										{t(stat.valueKey)}
									</div>
									<div className="text-sm text-black">{t(stat.labelKey)}</div>
								</div>
							))}
						</div>
					</div>

					{/* 右侧图片 - 错位布局 */}
					<div className="mt-8 lg:mt-0">
						<div className="relative">
							{/* 绿色底色装饰 - 位于右下角 */}
							<div className="absolute -bottom-4 right-0 z-0 h-[calc(100%-20px)] w-[calc(100%-20px)] rounded-xl bg-[#64a525] md:-bottom-5 md:-right-2 md:h-[calc(100%-24px)] md:w-[calc(100%-24px)]" />

							{/* 图片容器 - 位于左上角 */}
							<div className="relative z-10 mr-4 aspect-square w-[calc(100%-16px)] overflow-hidden rounded-xl md:mr-5 md:w-[calc(100%-20px)]">
								<Image
									src={factoryImages[currentIndex]}
									alt={t("factoryImageAlt")}
									width={1000}
									height={1000}
									className="h-full w-full object-cover transition-opacity duration-500"
									sizes="(max-width: 1024px) 100vw, 50vw"
								/>
							</div>
						</div>

						{/* 分页圆点 - 整体下方居中 */}
						<div className="mt-8 flex items-center justify-center gap-1.5 md:mt-10">
							{factoryImages.map((_, index) => (
								<button
									key={index}
									onClick={() => setCurrentIndex(index)}
									className={`h-2.5 w-2.5 rounded-full transition-colors duration-300 ${
										index === currentIndex ? "bg-mainColor" : "bg-[#7c7c7c]"
									}`}
									aria-label={`Go to slide ${index + 1}`}
								/>
							))}
						</div>
					</div>
				</div>
			</div>
		</section>
	);
}
